import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'dvRadar',
  name_en: 'DV Radar',
  // name_zh: 'DV 雷达图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    y: amount => amount.length > 2 && amount.length <= 10,
    x: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.ARRAY
    },
    x: {
      itemType: [DEFINE.STR, DEFINE.DATE],
      category: DEFINE.SINGLE,
      priority: DEFINE.DATE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
  };
}

export const dvRadar = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook, layout } = api;
    const presetOption = hook.buildStandardChartPresetOption(api);
    const {
      dataIndex = 0,
      encoding: { x, y }
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = api.hook.parseDataMetaInfo(data);
    const fontSize = presetOption.xAxis?.axisLabel?.fontSize || presetOption.textStyle.fontSize;
    const colorList = presetOption?.color || [];

    // * ------------------------- data
    const maxValues = {};
    data.values.forEach(item => {
      y.forEach(key => {
        maxValues[key] = Math.max(maxValues[key] || 0, item[key]);
      });
    });

    const legend = {
      data: data.values.map((item, index) => ({
        name: item.name,
        color: colorList[index % colorList.length] || 'blue',
        type: util.const.SERIES_2_LEGEND_TYPE_MAP.radar,
        selected: true,
        disableSelect: true
      })),
      show: false
    };

    const indicatorData = y.map(key => ({
      name: key,
      max: maxValues[key] + (maxValues[key] * 0.1 || 1)
    }));

    const seriesData = data.values.map(item => ({
      name: item[x],
      value: y.map(indicator => item[indicator])
    }));

    const { LAYOUT } = util.const;
    let fixedTextWidth = fontSize * 4;
    if (layout === LAYOUT.LARGE_3_2 || layout === LAYOUT.SMALL_2_1) {
      fixedTextWidth = fontSize * 7;
    }
    if (layout === LAYOUT.SMALL) {
      fixedTextWidth = fontSize * 3;
    }
    if (layout === LAYOUT.SMALL_1_2) {
      fixedTextWidth = fontSize * 2;
    }

    let radarRadius = '65%';
    if (layout.includes('lg')) {
      radarRadius = '85%';
    }
    if (layout === LAYOUT.SMALL_1_2) {
      radarRadius = '60%';
    }

    const originalRadar = {
      // ! 改动了源代码导致的
      indicator: [indicatorData[0], ...indicatorData.slice(1).reverse()].filter(Boolean),
      center: ['50%', '50%'],
      radius: radarRadius,
      axisName: {
        // width无法生效（ECharts bug）
        formatter: value => {
          const textWidth = util.canvas.measureTextWidth(
            value,
            fontSize,
            presetOption.textStyle.fontFamily
          );
          let newText: string = value;
          if (textWidth > fixedTextWidth) {
            while (
              util.canvas.measureTextWidth(newText, fontSize, presetOption.textStyle.fontFamily) >=
              fixedTextWidth
            ) {
              newText = newText.substring(0, newText.length - 1);
            }
            newText += '...';
          }
          return newText;
        }
      }
    };

    const radarOpt = api.util.data.deepMerge(api.token?.dvRadar || {}, originalRadar, {
      override: true
    });

    // * ------------------------- chart
    return {
      legend,
      xAxis: [],
      yAxis: [],
      radar: radarOpt,
      series: [
        {
          type: 'dvRadar',
          data: seriesData
        }
      ]
    };
  }
});
